import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight, Clock, RefreshCw } from 'lucide-react';
import Header from '../public/Header';
import Footer from '../public/Footer';
import { getSubscription } from '../../services/paymentService';
import { useTranslation } from '../../context/LanguageContext';

const POLL_INTERVAL = 3000; // 3 seconds
const MAX_POLL_TIME = 30000; // 30 seconds

const PaymentCallback = ({ onLogin }) => {
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const [status, setStatus] = useState('loading'); // 'loading', 'success', 'pending', 'error'
    const [message, setMessage] = useState('');
    const pollTimerRef = useRef(null);
    const startTimeRef = useRef(null);

    useEffect(() => {
        const subscriptionId = searchParams.get('subscriptionId');

        if (!subscriptionId) {
            setStatus('error');
            setMessage(t.payment?.noPaymentInfo || 'Aucune information de paiement trouvée.');
            return;
        }

        const userUuid = localStorage.getItem('userUuid');

        if (!userUuid) {
            // User not logged in (e.g. just registered) - show success with login prompt
            setStatus('success');
            setMessage(t.payment?.successLogin || 'Votre paiement a été enregistré. Connectez-vous pour accéder à votre espace.');
            return;
        }

        // Start polling
        startTimeRef.current = Date.now();
        pollSubscriptionStatus(userUuid);

        return () => {
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current);
            }
        };
    }, [searchParams]);

    const pollSubscriptionStatus = async (userUuid) => {
        try {
            const subscription = await getSubscription(userUuid);
            const isActive = subscription?.isActive ?? subscription?.ISACTIVE ?? subscription?.isactive ?? false;
            const subStatus = (subscription?.subscriptionStatus ?? subscription?.SUBSCRIPTIONSTATUS ?? '').toLowerCase();

            if (isActive || subStatus === 'active') {
                setStatus('success');
                setMessage(t.payment?.successMessage || 'Votre paiement a été traité avec succès. Votre abonnement est maintenant actif.');
                return;
            }

            if (subStatus === 'failed' || subStatus === 'canceled' || subStatus === 'expired') {
                setStatus('error');
                setMessage(t.payment?.failedMessage || 'Le paiement n\'a pas pu être traité. Veuillez réessayer.');
                return;
            }

            // Still pending - check timeout
            const elapsed = Date.now() - startTimeRef.current;
            if (elapsed >= MAX_POLL_TIME) {
                setStatus('pending');
                setMessage(t.payment?.pendingMessage || 'La vérification de votre paiement prend plus de temps que prévu. Votre abonnement sera activé dès confirmation.');
                return;
            }

            // Continue polling
            pollTimerRef.current = setTimeout(() => {
                pollSubscriptionStatus(userUuid);
            }, POLL_INTERVAL);

        } catch (error) {
            console.error('Error polling subscription status:', error);

            // On error, check timeout
            const elapsed = Date.now() - startTimeRef.current;
            if (elapsed >= MAX_POLL_TIME) {
                setStatus('pending');
                setMessage(t.payment?.pendingMessage || 'La vérification de votre paiement prend plus de temps que prévu. Votre abonnement sera activé dès confirmation.');
                return;
            }

            // Retry
            pollTimerRef.current = setTimeout(() => {
                pollSubscriptionStatus(userUuid);
            }, POLL_INTERVAL);
        }
    };

    const handleRetryCheck = () => {
        const userUuid = localStorage.getItem('userUuid');
        if (userUuid) {
            setStatus('loading');
            startTimeRef.current = Date.now();
            pollSubscriptionStatus(userUuid);
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Header onLogin={onLogin} />

            <main className="flex-1 flex items-center justify-center px-4 py-16">
                <div className="max-w-md w-full text-center">
                    {status === 'loading' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 mx-auto bg-cabaza-purple/10 rounded-full flex items-center justify-center">
                                <Loader2 className="w-10 h-10 text-cabaza-purple animate-spin" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                    {t.payment?.processing || 'Vérification en cours...'}
                                </h1>
                                <p className="text-gray-600">
                                    {t.payment?.processingDesc || 'Nous vérifions le statut de votre paiement auprès de Mollie.'}
                                </p>
                            </div>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-10 h-10 text-green-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                    {t.payment?.successTitle || 'Paiement réussi !'}
                                </h1>
                                <p className="text-gray-600 mb-6">
                                    {message}
                                </p>
                            </div>
                            <div className="space-y-3">
                                {localStorage.getItem('userUuid') ? (
                                    <Link
                                        to="/"
                                        className="inline-flex items-center gap-2 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                                    >
                                        {t.payment?.goToDashboard || 'Accéder à mon espace'}
                                        <ArrowRight className="w-5 h-5" />
                                    </Link>
                                ) : (
                                    <Link
                                        to="/login"
                                        className="inline-flex items-center gap-2 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                                    >
                                        {t.payment?.goToLogin || 'Se connecter'}
                                        <ArrowRight className="w-5 h-5" />
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}

                    {status === 'pending' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
                                <Clock className="w-10 h-10 text-amber-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                    {t.payment?.pendingTitle || 'Paiement en cours de traitement'}
                                </h1>
                                <p className="text-gray-600 mb-6">
                                    {message}
                                </p>
                            </div>
                            <div className="space-y-3">
                                <button
                                    onClick={handleRetryCheck}
                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                                >
                                    <RefreshCw className="w-5 h-5" />
                                    {t.payment?.retryCheck || 'Vérifier à nouveau'}
                                </button>
                                <p className="text-sm text-gray-500">
                                    {t.payment?.pendingNote || 'Vous pouvez aussi accéder à votre espace, l\'abonnement sera activé automatiquement.'}
                                </p>
                                <Link
                                    to="/"
                                    className="inline-flex items-center gap-2 text-cabaza-purple font-medium hover:underline"
                                >
                                    {t.payment?.goToDashboard || 'Accéder à mon espace'}
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                                <XCircle className="w-10 h-10 text-red-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                    {t.payment?.errorTitle || 'Paiement échoué'}
                                </h1>
                                <p className="text-gray-600 mb-6">
                                    {message || t.payment?.errorMessage || 'Une erreur est survenue lors du traitement de votre paiement.'}
                                </p>
                            </div>
                            <div className="space-y-3">
                                <Link
                                    to="/tarification"
                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                                >
                                    {t.payment?.retry || 'Réessayer'}
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                                <p className="text-sm text-gray-500">
                                    {t.payment?.contactSupport || 'Si le problème persiste, contactez notre support.'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default PaymentCallback;
